import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import { FILTER_KEYS } from '@app/consts';
import { ActiveFilterModel, defaultSearchFilters, SearchFilters } from '@app/models';

/**
 * Processes scalar/string-type filters and converts them to ActiveFilterModels
 * @returns Array containing a single ActiveFilterModel if value exists, empty array otherwise
 */
export const processFilter = <T extends number | string>(
    value: T | null | undefined,
    filterKey: string,
    categoryKey: string,
    getLabelFn: (value: T) => string,
): ActiveFilterModel[] => {
    if (
        value === null ||
        value === undefined ||
        (filterKey === FILTER_KEYS.ONLINE_BOOKING && value === '0')
    ) {
        return [];
    }

    return [
        {
            category: categoryKey,
            key: filterKey,
            value,
            label: getLabelFn(value),
        },
    ];
};

/**
 * Processes array-type filters and converts them to ActiveFilterModels
 * @returns Array of ActiveFilterModel objects created from the input values
 */
export const processArrayFilter = (
    values: string[] | undefined,
    filterKey: string,
    categoryKey: string,
    labelsMap: Record<number | string, string> | Array<{ value: number | string; label: string }>,
): ActiveFilterModel[] => {
    const result: ActiveFilterModel[] = [];

    values?.forEach(value => {
        const numericValue = !isNaN(Number(value)) ? Number(value) : value;

        let label: string;
        if (Array.isArray(labelsMap)) {
            // Handle array format: [{ value, label }, ...]
            const labelItem = labelsMap.find(item => item.value === numericValue);
            label = labelItem?.label || value;
        } else {
            // Handle object format: { key: value, ... }
            label = labelsMap[numericValue] || value;
        }

        result.push({
            category: categoryKey,
            key: filterKey,
            value,
            label,
        });
    });

    return result;
};

export const adjustOnlineBookingFilters = (
    oldFilters: SearchFilters,
    newFilters: SearchFilters,
) => {
    const adjustedFilters = cloneDeep(newFilters);

    const onlineBookingDependencyFilters = [
        FILTER_KEYS.PROPERTY_TYPE,
        FILTER_KEYS.AMENITIES,
        FILTER_KEYS.ROOM_EQUIPMENT,
        FILTER_KEYS.PRICE_FROM,
        FILTER_KEYS.PRICE_TO,
        FILTER_KEYS.PROVISION,
        FILTER_KEYS.RESERVATION_FEATURES,
    ];

    // onlineBooking is off
    if (
        (oldFilters.onlineBooking === '1' && newFilters.onlineBooking !== '1') ||
        // condition for partial filter handling
        (isEmpty(oldFilters) && newFilters.onlineBooking === null)
    ) {
        const filters = onlineBookingDependencyFilters.reduce(
            (acc, filter) => ({
                ...acc,
                [filter]: defaultSearchFilters[filter],
            }),
            adjustedFilters,
        );

        return {
            ...filters,
            sort: defaultSearchFilters.sort,
        };
    }

    const isOnlineBookingDependencyOn =
        !!newFilters.sort ||
        onlineBookingDependencyFilters.some(
            filter => !(isNil(newFilters[filter]) || isEmpty(newFilters[filter])),
        );

    // onlineBooking should be turned on
    if (isOnlineBookingDependencyOn && adjustedFilters.onlineBooking !== '1') {
        adjustedFilters.onlineBooking = '1';
    }

    return adjustedFilters;
};
